import { collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { env } from "../config/env";
import { db } from "../firebase";

export type EventItem = {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  description?: string;
  link?: string;
  startIso?: string;
  endIso?: string;
  allDay?: boolean;
};

const fallbackEvents: EventItem[] = [
  {
    id: "consultation-tour",
    title: "Consultation Visit and Tour",
    date: "2026-04-08",
    time: "By appointment",
    location: "Zion Classical Academy"
  },
  {
    id: "ptu-meeting",
    title: "Parent Teachers United Meeting",
    date: "2026-04-07",
    time: "Meal at 6:00 PM, meeting at 6:30 PM",
    location: "Zion Classical Academy"
  },
  {
    id: "track-field-day",
    title: "Spring Track & Field Day",
    date: "2026-05-07",
    time: "To be announced",
    location: "Hosted by Heartland Lutheran High School"
  }
];

const SCHOOL_TIME_ZONE = "America/Chicago";

function toIso(value: unknown) {
  try {
    const maybeTimestamp = value as { toDate?: () => Date; toMillis?: () => number } | null | undefined;
    if (!maybeTimestamp) return "";
    if (typeof maybeTimestamp.toDate === "function") return maybeTimestamp.toDate().toISOString();
    if (typeof maybeTimestamp.toMillis === "function") return new Date(maybeTimestamp.toMillis()).toISOString();
    const parsed = new Date(String(value));
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : "";
  } catch {
    return "";
  }
}

function dateOnly(iso: string) {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function parseCalendarApiDate(iso: string) {
  if (!iso) return null;
  const trimmed = iso.trim();
  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const parsed = new Date(hasTimezone ? trimmed : `${trimmed}Z`);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function timeLabel(iso: string, allDay?: boolean) {
  if (allDay) return "All Day";
  const parsed = parseCalendarApiDate(iso);
  if (!parsed) return "Time TBA";
  return parsed.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: SCHOOL_TIME_ZONE });
}

function centralDateLabel(iso: string) {
  const parsed = parseCalendarApiDate(iso);
  if (!parsed) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone: SCHOOL_TIME_ZONE,
  }).formatToParts(parsed);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return values.month && values.day && values.year
    ? `${values.month}/${values.day}/${values.year}`
    : "";
}

function mapEventDoc(id: string, data: Record<string, unknown>): EventItem {
  const startIso = String(data.startIso ?? "") || toIso(data.startAt) || toIso(data.start) || toIso(data.date);
  const endIso = String(data.endIso ?? "") || toIso(data.endAt) || toIso(data.end);
  const allDay = data.allDay === true;
  return {
    id,
    title: String(data.title ?? "School Event"),
    date: String(data.startDate ?? "") || dateOnly(startIso),
    time: String(data.timeLabel ?? data.startTime ?? "") || timeLabel(startIso, allDay),
    location: String(data.location ?? ""),
    description: String(data.description ?? ""),
    link: String(data.link ?? data.url ?? ""),
    startIso,
    endIso,
    allDay
  };
}

function mapCalendarApiItem(item: any): EventItem {
  const startIso = String(item.startIso ?? "");
  const allDay = item.allDay === true;
  return {
    id: String(item.id ?? item.uid ?? `${item.title}-${startIso}`),
    title: String(item.title ?? "School Event"),
    date: centralDateLabel(startIso) || String(item.startDate ?? "") || dateOnly(startIso),
    time: String(item.timeLabel ?? "") || timeLabel(startIso, allDay),
    location: String(item.location ?? ""),
    description: String(item.description ?? ""),
    link: String(item.link ?? ""),
    startIso,
    endIso: String(item.endIso ?? ""),
    allDay
  };
}

async function getCalendarApiEvents(limitCount: number) {
  if (!env.calendarApiUrl) return [];
  const url = new URL(env.calendarApiUrl);
  url.searchParams.set("action", "calendarEvents");
  url.searchParams.set("limit", String(limitCount));
  url.searchParams.set("onlyFuture", "1");

  const response = await fetch(url.toString());
  const payload = await response.json();
  if (!payload?.ok || !Array.isArray(payload.items)) return [];
  return payload.items.map(mapCalendarApiItem);
}

export async function getEvents(options: { useFallback?: boolean; limitCount?: number } = {}): Promise<EventItem[]> {
  const limitCount = Math.min(Math.max(options.limitCount ?? 25, 1), 100);

  if (db) {
    try {
      const now = new Date();
      const snap = await getDocs(
        query(
          collection(db, "events"),
          where("startAt", ">=", now),
          orderBy("startAt", "asc"),
          limit(limitCount)
        )
      );
      const items = snap.docs.map((item) => mapEventDoc(item.id, item.data()));
      if (items.length) return items;
    } catch (error) {
      console.warn("[public-web] Failed to load Firestore events; trying calendar API", error);
    }
  }

  try {
    const apiItems = await getCalendarApiEvents(limitCount);
    if (apiItems.length) return apiItems;
  } catch (error) {
    console.warn("[public-web] Failed to load calendar API events", error);
  }

  return options.useFallback === false ? [] : fallbackEvents;
}

export function getFallbackEvents() {
  return [
    ...fallbackEvents
  ];
}
